/**
 * components/analysis/AnalysisPanel.tsx
 * Fixed: all signal/plan values guarded against undefined before render.
 */

import { useState } from 'react';
import { CheckCircle, XCircle, Clock, TrendingUp } from 'lucide-react';
import clsx from 'clsx';
import { useTicker, useAccount } from '../../store';
import { tradesApi }             from '../../lib/api';
import { ScoreBreakdownCard, Signal } from '../shared/ScoreBreakdownCard';
import type { OptionsContract }  from '../../types';

// ── Safe number formatter ─────────────────────────────────────
// Prevents "can't access property toString on undefined"
function fmt(val: number | undefined | null, decimals = 2, prefix = ''): string {
  if (val === undefined || val === null || isNaN(val)) return '—';
  return `${prefix}${val.toFixed(decimals)}`;
}

function fmtPct(val: number | undefined | null, decimals = 1): string {
  if (val === undefined || val === null || isNaN(val)) return '—';
  return `${val >= 0 ? '+' : ''}${val.toFixed(decimals)}%`;
}

// ── Subcomponents ─────────────────────────────────────────────

function Metric({ label, value, sub, color }: {
  label: string; value: string; sub?: string;
  color?: 'bull' | 'bear' | 'amber' | 'sky';
}) {
  const cls = color === 'bull' ? 'text-bull' : color === 'bear' ? 'text-bear'
    : color === 'amber' ? 'text-amber' : color === 'sky' ? 'text-sky' : 'text-ink-1';
  return (
    <div className="stat-box">
      <div className="stat-label">{label}</div>
      <div className={clsx('stat-value', cls)}>{value}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function SkeletonRow() {
  return <div className="h-6 skeleton rounded mb-1" />;
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 text-ink-3 px-8 text-center">
      <TrendingUp size={40} className="opacity-20" />
      <div>
        <div className="text-sm font-medium text-ink-2 mb-1">No ticker selected</div>
        <div className="text-xs leading-relaxed">
          Run the pre-market scanner and select a candidate to see the full
          analysis — signals, options chain, trade plan, and AI coaching.
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

export function AnalysisPanel() {
  const ticker  = useTicker();
  const account = useAccount();
  const [isLogging, setIsLogging] = useState(false);

  if (!ticker.sym || !ticker.gapper) return <EmptyState />;

  const g       = ticker.gapper;
  const signals = ticker.signals;   // may be null while loading
  const chain   = ticker.chain;     // may be null while loading
  const plan    = ticker.plan;      // may be null while loading

  const scoreVal = g.score ?? 0;

  const otm1 = chain?.contracts?.find(c => c.classification === 'otm-1') ?? null;

  const catalystBadgeCls = {
    earnings: 'badge-bull',
    fda:      'badge-violet',
    upgrade:  'badge-sky',
    contract: 'badge-sky',
    macro:    'badge-amber',
    social:   'badge-amber',
    unknown:  'badge-amber',
  }[g.catalystType ?? 'unknown'] ?? 'badge-amber';

  async function logTrade() {
    if (!plan || !otm1 || isLogging) return;
    setIsLogging(true);
    try {
      const trade = await tradesApi.create({
        sym:                 g.sym,
        contractDescription: `${g.sym} $${otm1.strike}C ${chain?.expirations?.[0] ?? ''}`,
        catalystSummary:     g.catalystSummary,
        catalystType:        g.catalystType,
        entryStockPrice:     g.price,
        entryOptionPrice:    otm1.ask,
        contracts:           plan.contractCount,
        plannedStop:         plan.stopPrice,
        plannedTarget1:      plan.targetPrice,
        plannedRR:           plan.riskRewardRatio,
        score:               scoreVal,
      });
      account.addTrade(trade);
      alert(`✓ Trade logged: ${g.sym} $${otm1.strike}C`);
    } catch {
      alert('Failed to log trade — check server connection');
    } finally {
      setIsLogging(false);
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <div className="font-display text-3xl font-extrabold tracking-tight leading-none">
            {g.sym}
          </div>
          <div className="text-xs text-ink-2 mt-1 flex items-center gap-2">
            {g.name}
            <span className={clsx('badge', catalystBadgeCls)}>
              {(g.catalystType ?? 'unknown').toUpperCase()}
            </span>
          </div>
          <div className="flex items-baseline gap-3 mt-2">
            <span className="text-2xl font-mono font-medium">
              ${fmt(g.price)}
            </span>
            <span className="text-sm font-mono text-bull">
              {fmtPct(g.changePct)} pre-mkt
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          {plan && otm1 && (
            <button onClick={logTrade} disabled={isLogging} className="btn-primary">
              {isLogging ? 'Logging…' : '+ Log Trade'}
            </button>
          )}
          <button className="btn-ghost">Skip →</button>
        </div>
      </div>

      {/* Catalyst summary */}
      {g.catalystSummary && g.catalystSummary !== 'Run analysis for catalyst details' && (
        <div className="p-3 bg-bg-2 rounded-lg text-xs text-ink-2 leading-relaxed border border-border">
          <span className="text-ink-3 font-mono text-2xs uppercase tracking-widest">
            Catalyst
          </span>
          <br />
          {g.catalystSummary}
        </div>
      )}

      {/* Metric strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-2">
        <Metric
          label="Rel. Volume"
          value={g.rvol != null ? `${g.rvol}×` : '—'}
          sub={g.rvol >= 2 ? 'Strong ✓' : 'Moderate'}
          color={g.rvol >= 2 ? 'bull' : 'amber'}
        />
        <Metric
          label="RSI (15m)"
          value={fmt(signals?.rsi14, 1)}
          sub={signals == null ? 'Loading…'
            : signals.rsiInMomentumZone ? '✓ In zone' : '⚠ Off zone'}
          color={signals?.rsiInMomentumZone ? 'bull' : 'amber'}
        />
        <Metric
          label="ATR (14)"
          value={signals ? `$${fmt(signals.atr14)}` : '—'}
          sub={signals ? `${fmt(signals.atr14 / g.price * 100, 1)}% of price` : 'Loading…'}
        />
        <Metric
          label="Stop (0.75×)"
          value={plan ? `$${fmt(plan.stopPrice)}` : '—'}
          sub="ATR below entry"
          color="bear"
        />
        <Metric
          label="R:R"
          value={plan ? `${fmt(plan.riskRewardRatio, 1)}:1` : '—'}
          sub={plan ? `${fmt(plan.breakEvenWinRate, 0)}% WR needed` : 'Loading…'}
          color={plan && plan.riskRewardRatio >= 2 ? 'bull' : 'amber'}
        />
      </div>

      {/* Signals + Options chain */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-4">

        {/* Momentum signals */}
        <div className="card">
          <div className="section-label mb-3">Momentum Signals</div>
          {signals ? (
            <>
              <Signal label="Above VWAP"
                passed={signals.aboveVwap ?? false}
                value={signals.aboveVwap
                  ? `✓ $${fmt(signals.vwap)}`
                  : `✗ $${fmt(signals.vwap)}`} />
              <Signal label="MACD histogram"
                passed={signals.macdBullish ?? false}
                value={`${signals.macdBullish ? '✓' : '✗'} ${fmt(signals.macdHistogram, 4)}`} />
              <Signal label="RSI 55–72 zone"
                passed={signals.rsiInMomentumZone ?? false}
                value={fmt(signals.rsi14, 1)} />
              <Signal label="9 EMA > 21 EMA"
                passed={signals.ema9AboveEma21 ?? false}
                value={signals.ema9AboveEma21 ? '✓ Aligned' : '✗ Crossed'} />
              <Signal label="Volume surge"
                passed={(g.rvol ?? 0) >= 1.5}
                value={`${g.rvol ?? '—'}× average`} />
              <div className="signal-row border-0 mt-1">
                <span className="text-xs text-ink-3">15-min candle close</span>
                <span className="flex items-center gap-1 text-2xs text-ink-3 font-mono">
                  <Clock size={10} /> Wait for open
                </span>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              {[...Array(6)].map((_, i) => <SkeletonRow key={i} />)}
            </div>
          )}
        </div>

        {/* Options chain */}
        <div className="card overflow-x-auto">
          <div className="section-label mb-3">
            Options Chain — Weekly Calls
            {chain?.expirations?.[0] && (
              <span className="ml-2 text-ink-3 normal-case tracking-normal">
                ({chain.expirations[0]})
              </span>
            )}
          </div>
          {chain?.contracts?.length ? (
            <>
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="chain-th text-left">Strike</th>
                    <th className="chain-th">Δ</th>
                    <th className="chain-th">Bid</th>
                    <th className="chain-th">Ask</th>
                    <th className="chain-th">Spread</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Center display around ATM — show 2 ITM + ATM + 4 OTM
                    // instead of first 6 strikes which are always deep ITM
                    const all = chain.contracts;
                    const atmIdx = all.findIndex(c =>
                      c.classification === 'atm' || c.classification === 'otm-1'
                    );
                    const start = atmIdx > 2 ? atmIdx - 2 : 0;
                    return all.slice(start, start + 7);
                  })().map((c: OptionsContract) => {
                    const isSelected = c.classification === 'otm-1';
                    const spreadColor = c.spread <= 0.10 ? 'text-bull'
                      : c.spread <= 0.15 ? 'text-amber' : 'text-bear';
                    const labelMap: Record<string, string> = {
                      'deep-itm': 'Deep ITM', 'itm': 'ITM', 'atm': 'ATM',
                      'otm-1': '★ 1 OTM', 'otm-2': '2 OTM', 'deep-otm': 'Deep OTM',
                    };
                    const labelColorMap: Record<string, string> = {
                      'otm-1': 'text-gold', 'atm': 'text-sky',
                      'itm': 'text-ink-2', 'deep-itm': 'text-ink-2',
                    };
                    return (
                      <tr key={c.strike} className={clsx('chain-row', isSelected && 'selected')}>
                        <td className={clsx('chain-td', labelColorMap[c.classification] ?? 'text-ink-3')}>
                          {labelMap[c.classification] ?? c.classification} ${c.strike}
                        </td>
                        <td className="chain-td">{fmt(c.delta, 2)}</td>
                        <td className="chain-td">${fmt(c.bid)}</td>
                        <td className="chain-td">${fmt(c.ask)}</td>
                        <td className={clsx('chain-td', spreadColor)}>${fmt(c.spread)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {otm1 && (
                <div className="mt-3 pt-3 border-t border-border text-2xs text-ink-3">
                  ★ Rec: ${otm1.strike}C · Ask ${fmt(otm1.ask)} · Mid ${fmt(otm1.mid)} · OI {(otm1.openInterest ?? 0).toLocaleString()}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => <div key={i} className="h-7 skeleton" />)}
            </div>
          )}
        </div>
      </div>

      {/* Score meter */}
      <ScoreBreakdownCard score={scoreVal} scoreBreakdown={g.scoreBreakdown} />

      {/* Trade plan */}
      {plan && (
        <div className="card">
          <div className="section-label mb-3">
            Trade Plan — {g.sym} ${otm1?.strike ?? '—'}C (1 OTM, Weekly)
          </div>
          <div>
            {[
              { label: 'Entry trigger',  value: plan.entryTrigger ?? '—',                      color: ''         },
              { label: 'Option',         value: otm1 ? `$${otm1.strike}C · Ask $${fmt(otm1.ask)} · Δ ${fmt(otm1.delta)}` : '—', color: '' },
              { label: 'Order type',     value: otm1 ? `Limit at mid $${fmt((otm1.bid + otm1.ask) / 2)} · max $${fmt(otm1.ask)}` : '—', color: '' },
              { label: 'Stock stop',     value: `$${fmt(plan.stopPrice)} (0.75× ATR)`,          color: 'text-bear' },
              { label: 'Option stop',    value: 'Exit if option loses 50% of premium',          color: 'text-bear' },
              { label: 'Target 1 (50%)', value: plan.target1Rule ?? '—',                        color: 'text-bull' },
              { label: 'Target 2 (50%)', value: `${plan.target2Rule ?? '—'} → $${fmt(plan.targetPrice)}`, color: 'text-bull' },
              { label: 'Breakeven',      value: `$${fmt(plan.breakeven)} (strike + premium)`,  color: ''         },
              { label: 'Contracts',      value: `${plan.contractCount ?? 1} (2% rule)`,        color: ''         },
              { label: 'Max loss',       value: `-$${fmt(plan.maxLoss)}`,                      color: 'text-bear' },
              { label: 'R:R',            value: `${fmt(plan.riskRewardRatio, 1)}:1 · win ${fmt(plan.breakEvenWinRate, 0)}%+ to profit`, color: 'text-bull' },
              { label: 'Time limit',     value: 'Close all by 10:30 AM if not at target',      color: 'text-amber' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex flex-col gap-0.5 sm:flex-row sm:justify-between sm:items-start py-2 border-b border-border last:border-0">
                <span className="text-2xs text-ink-3 font-mono uppercase tracking-widest sm:min-w-[110px]">
                  {label}
                </span>
                <span className={clsx('text-xs font-mono sm:text-right sm:flex-1 sm:ml-4 break-words', color || 'text-ink-1')}>
                  {value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
